import { FormEvent, useEffect, useMemo, useRef, useState } from 'react'
import { Mic, Play, Send, Sparkles, X } from 'lucide-react'
import {
  askExplainQuestion,
  fetchExplainSummary,
  type ChartExplanation,
  type ExplainLanguage,
  type ExplanationCitation,
} from '../api/explain'
import { useLang, t as tx, fmt as fmtx } from '../lib/i18n'

type ChatTurn = {
  id: string
  role: 'assistant' | 'user'
  text: string
  citations: ExplanationCitation[]
  audioUrl?: string | null
  label?: string
}

type SpeechRecognitionLike = {
  lang: string
  interimResults: boolean
  maxAlternatives: number
  onresult: ((event: { results: Array<Array<{ transcript: string }>> }) => void) | null
  onerror: ((event: unknown) => void) | null
  start: () => void
}

type SpeechRecognitionCtor = new () => SpeechRecognitionLike

function voiceForLanguage(language: ExplainLanguage) {
  return language === 'ar' ? 'ar-SY-AmanyNeural' : 'en-US-AriaNeural'
}

// Every string the panel renders, in both languages. Arabic is patient-friendly
// Modern Standard Arabic (clear register for healthcare), not a literal calque.
const UI = {
  en: {
    badge: 'Explain My Chart',
    title: 'Chart explanation for patient',
    voiceLabel: 'Voice',
    closeAria: 'Close explainer',
    micAria: 'Use microphone',
    playSummary: 'Play summary',
    summaryLabel: 'Summary',
    sources: 'Sources',
    replayAudio: 'Replay audio',
    askHeading: 'Ask a question',
    yourQuestion: 'Your question',
    askPlaceholder: 'Ask about meds, labs, or diagnoses...',
    askSubmit: 'Ask chart',
    asking: 'Asking…',
    conversation: 'Conversation',
    questionTurn: 'Question',
    answerTurn: 'Answer',
    emptyConversation: 'Ask a follow-up and the conversation will appear here.',
    loadFailed: 'We could not load the chart explanation. Please retry.',
    retry: 'Retry',
    speechHelp:
      'Web Speech API is used for microphone capture when the browser supports it. Otherwise, type the question manually.',
    speechUnsupported: 'Speech input is not available in this browser.',
    listening: 'Listening for a short question...',
    transcribed: 'Transcript inserted into the question box.',
    speechFailed: 'Speech capture failed, please type the question.',
    footer: 'Answers stay read-only and cite only the sections returned by the explanation endpoint.',
  },
  ar: {
    badge: 'اشرح لي ملفي',
    title: 'شرح مبسّط لملفك الصحي',
    voiceLabel: 'الصوت',
    closeAria: 'إغلاق الشرح',
    micAria: 'استخدام الميكروفون',
    playSummary: 'استمع إلى الملخّص',
    summaryLabel: 'الملخّص',
    sources: 'المصادر',
    replayAudio: 'إعادة الاستماع',
    askHeading: 'اطرح سؤالاً',
    yourQuestion: 'سؤالك',
    askPlaceholder: 'اكتب سؤالك عن دوائك أو تحاليلك أو أي تفصيلة في ملفك…',
    askSubmit: 'إرسال السؤال',
    asking: 'جارٍ الإرسال…',
    conversation: 'المحادثة',
    questionTurn: 'سؤالك',
    answerTurn: 'الإجابة',
    emptyConversation: 'اطرح سؤالك التالي وستظهر المحادثة هنا.',
    loadFailed: 'تعذّر تحميل شرح الملف. الرجاء إعادة المحاولة.',
    retry: 'إعادة المحاولة',
    speechHelp: 'يمكنك السؤال عبر الميكروفون إذا كان متصفحك يدعم هذه الميزة، وإلا فاكتب سؤالك مباشرة.',
    speechUnsupported: 'التعرّف على الصوت غير مدعوم في هذا المتصفح، ويمكنك كتابة سؤالك بدل ذلك.',
    listening: 'أستمع إليك… تفضّل بطرح سؤالك.',
    transcribed: 'أُضيف النصّ إلى خانة السؤال.',
    speechFailed: 'تعذّر تسجيل الصوت، الرجاء كتابة السؤال.',
    footer: 'جميع الإجابات للقراءة فقط وتستند إلى أقسام ملفّك المذكورة، دون أي تعديل عليها.',
  },
} as const

function citationLabel(citation: ExplanationCitation) {
  return citation.section
}

function playAudio(url?: string) {
  if (!url) return
  const audio = new Audio(url)
  void audio.play().catch(() => {})
}

export function ExplainMyChartLauncher({
  patientId,
  patientName,
}: {
  patientId: string
  patientName: string
}) {
  const [open, setOpen] = useState(false)

  return (
    <>
      <section className="rounded-xl border border-emerald-200 bg-gradient-to-r from-emerald-50 via-white to-stone-50 p-5 shadow-soft">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-white px-3 py-1 text-xs font-semibold text-emerald-800">
              <Sparkles className="h-3.5 w-3.5" />
              {tx('emc.badge')}
            </div>
            <h3 className="mt-3 text-2xl font-semibold text-stone-950">{tx('emc.heading')}</h3>
            <p className="mt-2 text-sm leading-6 text-stone-600">
              {fmtx(tx('emc.desc'), { name: patientName })}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-primary-700 px-4 text-sm font-semibold text-white shadow-md shadow-emerald-900/15 transition hover:bg-primary-800 active:scale-95"
          >
            <Sparkles className="h-4 w-4" />
            {tx('emc.open')}
          </button>
        </div>
      </section>

      {open && (
        <ExplainMyChartPanel patientId={patientId} onClose={() => setOpen(false)} />
      )}
    </>
  )
}

function ExplainMyChartPanel({
  patientId,
  onClose,
}: {
  patientId: string
  onClose: () => void
}) {
  const [language, setLanguage] = useState<ExplainLanguage>('en')
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [summary, setSummary] = useState<ChatTurn | null>(null)
  const [turns, setTurns] = useState<ChatTurn[]>([])
  const [question, setQuestion] = useState('')
  const [asking, setAsking] = useState(false)
  const [speechNote, setSpeechNote] = useState('')
  const [reloadNonce, setReloadNonce] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setLoadError(false)
    fetchExplainSummary(patientId, language)
      .then((response: ChartExplanation) => {
        if (cancelled) return
        const nextSummary: ChatTurn = {
          id: `summary-${language}`,
          role: 'assistant',
          label: 'Summary',
          text: response.explanation_text,
          citations: response.citations,
          audioUrl: response.audio_url,
        }
        setSummary(nextSummary)
        setTurns([nextSummary])
      })
      .catch(() => {
        if (cancelled) return
        setSummary(null)
        setTurns([])
        setLoadError(true)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [language, patientId, reloadNonce])

  const voice = useMemo(() => voiceForLanguage(language), [language])
  const t = UI[language]

  function startSpeechCapture() {
    const speechWindow = window as Window & {
      SpeechRecognition?: SpeechRecognitionCtor
      webkitSpeechRecognition?: SpeechRecognitionCtor
    }
    const SpeechCtor = speechWindow.SpeechRecognition ?? speechWindow.webkitSpeechRecognition
    const tr = UI[language]

    if (!SpeechCtor) {
      setSpeechNote(tr.speechUnsupported)
      return
    }

    const recognition = new SpeechCtor()
    recognition.lang = language === 'ar' ? 'ar-SY' : 'en-US'
    recognition.interimResults = false
    recognition.maxAlternatives = 1
    recognition.onresult = (event) => {
      const transcript = event.results?.[0]?.[0]?.transcript ?? ''
      setQuestion(transcript)
      setSpeechNote(tr.transcribed)
    }
    recognition.onerror = () => {
      setSpeechNote(tr.speechFailed)
    }
    recognition.start()
    setSpeechNote(tr.listening)
  }

  async function submit(event: FormEvent) {
    event.preventDefault()
    const trimmed = question.trim()
    if (!trimmed || asking) return
    setAsking(true)
    setSpeechNote('')
    const userTurn: ChatTurn = {
      id: `user-${Date.now()}`,
      role: 'user',
      text: trimmed,
      citations: [],
    }
    setTurns((current) => [...current, userTurn])
    setQuestion('')
    try {
      const response = await askExplainQuestion(patientId, { question: trimmed, language })
      const assistantTurn: ChatTurn = {
        id: `answer-${Date.now()}`,
        role: 'assistant',
        label: 'Answer',
        text: response.explanation_text,
        citations: response.citations,
        audioUrl: response.audio_url,
      }
      setTurns((current) => [...current, assistantTurn])
    } catch {
      setLoadError(true)
    } finally {
      setAsking(false)
      inputRef.current?.focus()
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-stone-950/40 p-4 backdrop-blur-sm">
      <div className="mx-auto flex h-full w-full max-w-5xl items-center justify-center">
        <section
          dir={language === 'ar' ? 'rtl' : 'ltr'}
          lang={language === 'ar' ? 'ar' : 'en'}
          className="flex max-h-[92vh] w-full flex-col overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-lift-lg"
        >
          <div className="flex items-start justify-between gap-4 border-b border-stone-100 px-5 py-4">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-800">
                <Sparkles className="h-3.5 w-3.5" />
                {t.badge}
              </div>
              <h3 className="mt-2 text-xl font-semibold text-stone-950">{t.title}</h3>
              <p className="mt-1 text-sm text-stone-500">
                {t.voiceLabel}: {voice}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label={t.closeAria}
              className="grid h-9 w-9 place-items-center rounded-lg border border-stone-200 text-stone-600 transition hover:bg-stone-50 active:scale-90"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="grid min-h-0 flex-1 gap-4 overflow-hidden p-5 lg:grid-cols-[1.15fr_0.85fr]">
            <div className="flex min-h-0 flex-col gap-4">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 rounded-lg border border-stone-200 bg-stone-50 p-1">
                  <button
                    type="button"
                    onClick={() => setLanguage('en')}
                    className={`rounded-md px-3 py-1.5 text-sm font-semibold transition ${
                      language === 'en' ? 'bg-primary-700 text-white' : 'text-stone-600'
                    }`}
                  >
                    English
                  </button>
                  <button
                    type="button"
                    onClick={() => setLanguage('ar')}
                    className={`rounded-md px-3 py-1.5 text-sm font-semibold transition ${
                      language === 'ar' ? 'bg-primary-700 text-white' : 'text-stone-600'
                    }`}
                  >
                    العربية
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => summary?.audioUrl && playAudio(summary.audioUrl)}
                  className="inline-flex items-center gap-2 rounded-lg border border-stone-200 px-3 py-2 text-sm font-semibold text-stone-700 transition hover:border-emerald-200 hover:text-emerald-800 disabled:opacity-50"
                  disabled={!summary?.audioUrl}
                >
                  <Play className="h-4 w-4" />
                  {t.playSummary}
                </button>
              </div>

              <div className="min-h-0 flex-1 overflow-auto rounded-xl border border-stone-200 bg-stone-50/60 p-4">
                {loading && (
                  <div className="space-y-3">
                    <div className="skeleton h-5 w-40" />
                    <div className="skeleton h-4 w-full" />
                    <div className="skeleton h-4 w-5/6" />
                  </div>
                )}

                {!loading && loadError && (
                  <div className="rounded-xl border border-rose-200 bg-rose-50 p-4">
                    <p className="text-sm font-medium text-rose-900">{t.loadFailed}</p>
                    <button
                      type="button"
                      onClick={() => setReloadNonce((nonce) => nonce + 1)}
                      className="mt-3 inline-flex h-9 items-center gap-2 rounded-lg border border-rose-300 bg-white px-3 text-sm font-semibold text-rose-800 transition hover:bg-rose-100"
                    >
                      {t.retry}
                    </button>
                  </div>
                )}

                {!loading && !loadError && summary && (
                  <article className="rounded-xl border border-emerald-200 bg-white p-4 shadow-sm">
                    <div className="mb-2 flex items-center justify-between gap-3">
                      <div className="text-xs font-semibold uppercase tracking-wide text-stone-400">{t.summaryLabel}</div>
                      <button
                        type="button"
                        onClick={() => playAudio(summary.audioUrl ?? undefined)}
                        className="inline-flex items-center gap-1.5 rounded-full border border-stone-200 px-3 py-1 text-xs font-semibold text-stone-600 transition hover:border-emerald-200 hover:text-emerald-800"
                      >
                        <Play className="h-3.5 w-3.5" />
                        {t.replayAudio}
                      </button>
                    </div>
                    <p className="whitespace-pre-wrap text-sm leading-6 text-stone-800">{summary.text}</p>
                    {summary.citations.length > 0 && (
                      <div className="mt-3 flex flex-wrap items-center gap-2">
                        <span className="text-xs font-semibold uppercase tracking-wide text-stone-400">{t.sources}</span>
                        {summary.citations.map((citation) => (
                          <span
                            key={`${summary.id}-${citation.section}`}
                            className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-800"
                            title={citation.items_used.join(' · ')}
                          >
                            {citationLabel(citation)}
                          </span>
                        ))}
                      </div>
                    )}
                  </article>
                )}
              </div>
            </div>

            <aside className="flex min-h-0 flex-col gap-4 overflow-hidden rounded-xl border border-stone-200 bg-white p-4">
              <form onSubmit={submit} className="space-y-3">
                <div className="text-sm font-semibold text-stone-950">{t.askHeading}</div>
                <label className="grid gap-2 text-sm text-stone-600">
                  {t.yourQuestion}
                  <div className="flex items-center gap-2 rounded-lg border border-stone-200 bg-white px-3 py-2 focus-within:border-emerald-500 focus-within:ring-4 focus-within:ring-emerald-100">
                    <input
                      ref={inputRef}
                      value={question}
                      onChange={(event) => setQuestion(event.target.value)}
                      placeholder={t.askPlaceholder}
                      className="w-full bg-transparent text-stone-950 outline-none placeholder:text-stone-400"
                    />
                    <button
                      type="button"
                      onClick={startSpeechCapture}
                      aria-label={t.micAria}
                      className="grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-stone-200 text-stone-600 transition hover:border-emerald-200 hover:text-emerald-800"
                    >
                      <Mic className="h-4 w-4" />
                    </button>
                  </div>
                </label>
                <button
                  type="submit"
                  disabled={asking || !question.trim()}
                  className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-primary-700 px-4 text-sm font-semibold text-white shadow-md shadow-emerald-900/15 transition hover:bg-primary-800 active:scale-95 disabled:opacity-60"
                >
                  <Send className="h-4 w-4" />
                  {asking ? t.asking : t.askSubmit}
                </button>
              </form>

              <div className="rounded-lg border border-dashed border-stone-200 bg-stone-50 p-3 text-xs leading-5 text-stone-500">
                {t.speechHelp}
              </div>

              {speechNote && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                  {speechNote}
                </div>
              )}

              <div className="min-h-0 flex-1 overflow-auto">
                <div className="mb-2 text-sm font-semibold text-stone-950">{t.conversation}</div>
                <div className="space-y-3">
                  {turns.slice(1).length === 0 ? (
                    <div className="rounded-lg border border-stone-200 bg-stone-50 p-4 text-sm text-stone-500">
                      {t.emptyConversation}
                    </div>
                  ) : (
                    turns.slice(1).map((turn) => (
                      <article
                        key={turn.id}
                        className={`rounded-lg border p-3 ${
                          turn.role === 'assistant' ? 'border-emerald-200 bg-emerald-50/60' : 'border-stone-200 bg-stone-50'
                        }`}
                      >
                        <div className="mb-2 flex items-center justify-between gap-3">
                          <div className="text-xs font-semibold uppercase tracking-wide text-stone-400">
                            {turn.role === 'user' ? t.questionTurn : t.answerTurn}
                          </div>
                          {turn.role === 'assistant' && (
                            <button
                              type="button"
                              onClick={() => playAudio(turn.audioUrl ?? undefined)}
                              className="inline-flex items-center gap-1.5 rounded-full border border-stone-200 px-3 py-1 text-xs font-semibold text-stone-600 transition hover:border-emerald-200 hover:text-emerald-800"
                            >
                              <Play className="h-3.5 w-3.5" />
                              {t.replayAudio}
                            </button>
                          )}
                        </div>
                        <p className="whitespace-pre-wrap text-sm text-stone-800">{turn.text}</p>
                        {turn.role === 'assistant' && turn.citations.length > 0 && (
                          <div className="mt-3 flex flex-wrap items-center gap-2">
                            <span className="text-xs font-semibold uppercase tracking-wide text-stone-400">{t.sources}</span>
                            {turn.citations.map((citation) => (
                              <span
                                key={`${turn.id}-${citation.section}`}
                                className="inline-flex items-center rounded-full border border-emerald-200 bg-white px-2.5 py-1 text-xs font-semibold text-emerald-800"
                                title={citation.items_used.join(' · ')}
                              >
                                {citationLabel(citation)}
                              </span>
                            ))}
                          </div>
                        )}
                      </article>
                    ))
                  )}
                </div>
              </div>

              <div className="text-xs leading-5 text-stone-400">
                {t.footer}
              </div>
            </aside>
          </div>
        </section>
      </div>
    </div>
  )
}
